import DropDownBreadcrumbWrapper from '@anm/components/DropDownBreadcrumb/Wrapper';
import { LogoWrapper } from '@anm/components/Logo/Wrapper';
import TitleBlock from '@anm/components/TitleBlock';
import { TooltipContent } from '@anm/components/Tooltip';
import Button from '@anm/components/buttons/Button/Button';
import Container from '@anm/components/buttons/Button/Container';
import {
  OptionContainer,
  SelectOptionsContainer,
  SelectValueContainer,
  SelectWrapper
} from '@anm/components/select/Select';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import {
  CUSTOMIZATION_CONTENT_COLUMN_MAX_WIDTH,
  DASHBOARD_CONTENT_WRAPPER_WIDTH,
  PROJECT_PAGE_NAVBAR_WIDTH
} from '@anm/styles/constants';
import { blackFilter, flexColumn, media, truncate, whiteShadowNavButtonActiveStyles } from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

import RightPanelWrapper from '../../hosting/CustomizationSection/RightPanel/Wrapper';
import { MyVideosTitleWrapper } from '../../my-projects/ProjectBreadcrumbs/MyVideosTitle';

import { ButtonStyledProps, LeftBarVariant } from './types';

type WrapperProps = {
  variant: LeftBarVariant;
  scrollDirection: boolean;
  isScrolled?: boolean;
  isPadding?: boolean;
};

const myProjectsContentWrapperAdaptive = media.tablet`
    width: 100%;
    max-width: 100%;
    padding: ${({ isPadding }: WrapperProps) => (isPadding ? `26px 20px` : '0')};
    
`;

const navbarSmallScreen = media.desktop_small`
  width: auto;
  z-index: 7;
`;

const navbarTablet = media.tablet`
  padding: 17px 14px 25px;
  border: 0 none;
  width: 100%;
`;

const HeaderWrapperAdaptiveStyles = media.tablet`
  ${flexColumn};
  padding: 17px 20px 25px;
  transform: translateY(-400%);
  transition: all 0.4s ease-out;
  ${DropDownBreadcrumbWrapper}, ${MyVideosTitleWrapper} {
    margin-bottom: 20px;
  }
`;

export const HeaderWrapper = styled.div`
  ${flexColumn};
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  position: sticky;
  top: 0;
  padding: 27px 48px 0 48px;
  background-color: #fff;
  z-index: 134;
  ${DropDownBreadcrumbWrapper}, ${MyVideosTitleWrapper} {
    margin-bottom: 36px;
  }
  ${HeaderWrapperAdaptiveStyles};
`;

const headerWrapperScrolledStyles = css`
  ${HeaderWrapper} {
    box-shadow: rgb(1 83 130 / 10%) 0 2px 10px;
  }
`;

const tabletScrollToTopHeaderWrapperStyles = media.tablet`
  ${HeaderWrapper} {
    top: 131px;
    transform: translateY(0);
  }
`;

export const ContentWrapper = styled.div<WrapperProps>`
  position: relative;
  z-index: 6;
  ${myProjectsContentWrapperAdaptive};
  ${({ variant }) => contentWrapperVariants[variant]};
  ${({ isScrolled }) => isScrolled && headerWrapperScrolledStyles};
  ${({ scrollDirection }) => !scrollDirection && tabletScrollToTopHeaderWrapperStyles};
`;

export const LeftBarContent = styled.div`
  height: 100%;
`;

export const Navbar = styled.nav`
  z-index: 5;
  position: relative;
  padding: 18px 20px 27px 20px;
  border-right: 1px solid #ccdde6;
  width: 100%;
  max-width: 298px;
  ${navbarSmallScreen};
  ${navbarTablet};
  ${LogoWrapper} {
    display: inline-block;
  }
`;

const DashboardTitleBlockAdaptiveStyles = media.tablet`
    margin: 0;
`;

export const DashboardTitleBlock = styled(TitleBlock)`
  letter-spacing: 0.01em;
  margin: 13px 10px 15px;
  overflow: hidden;
  flex: 1;
  h1,
  h2,
  h3,
  h4,
  h5 {
    ${truncate('auto')};
  }
  ${DashboardTitleBlockAdaptiveStyles};
`;

const HeaderButtonsWrapperAdaptiveStyles = media.tablet`
  & > * {
    padding: 0;
  }
  & > *, ${Button}, ${Container}{
    min-width: 142px;
    flex: 1;
    width: 100%;
    max-width: initial;
  }

  &:empty {
      display: none;
  }
`;

const HeaderButtonsWrapperPhone = media.phone`
  width: 100%;
`;

export const HeaderButtonsWrapper = styled.div`
  gap: 10px;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: fit-content;
  margin-bottom: 10px;

  ${HeaderButtonsWrapperPhone};
  ${HeaderButtonsWrapperAdaptiveStyles};
`;

export type NavButtonVariant = 'white' | 'white-shadow';

const whiteActiveStyles = css`
  font-weight: bold;
  background-color: #f5f7fa;
`;

export const variants: { [key in NavButtonVariant]: ReturnType<typeof css> } = {
  white: css`
    &:hover {
      background-color: var(--gray_20);
    }
  `,
  'white-shadow': css`
    color: var(--gray_500);
    font-weight: 700;
    border-radius: 10px;
    &:hover {
      ${whiteShadowNavButtonActiveStyles}
    }
  `
};
export const NavButton = styled.div<ButtonStyledProps>`
  color: #292a2b;
  font-size: 16px;
  cursor: pointer;
  line-height: 50px;
  border-radius: 5px;
  margin-bottom: 10px;
  align-items: center;
  position: relative;
  white-space: nowrap;
  display: inline-flex;
  padding: 0 19px 0 10px;
  ${({ navButtonVariant }) => variants[navButtonVariant || 'white']}
  ${({ isActive, navButtonVariant }) => isActive && navButtonVariant === 'white' && whiteActiveStyles}
  ${({ isActive, navButtonVariant }) =>
    isActive && navButtonVariant === 'white-shadow' && whiteShadowNavButtonActiveStyles}

  img {
    margin-right: 10px;
    width: 24px;
    object-fit: none;
    filter: ${({ isActive }) => (!isActive ? 'none' : `${blackFilter}`)};
  }

  &:empty {
    display: none;
  }
`;

export const LinkButton = styled.div`
  a {
    display: block;
  }
`;

const buttonsWrapperTablet = media.tablet`
  margin: 5px;
`;

export const ButtonsWrapper = styled.div`
  top: 90px;
  z-index: 4;
  display: flex;
  position: sticky;
  flex-direction: column;

  ${TooltipContent} {
    left: unset;
    right: -10px;
    min-width: 245px;
    padding: 16px 20px 18px;
    line-height: 20px;
  }
  ${buttonsWrapperTablet};
`;

const rightPanelWrapperOnTablet = media.tablet`
  max-width: ${CUSTOMIZATION_CONTENT_COLUMN_MAX_WIDTH} !important;
`;

const contentWrapperVariants: {
  [key in LeftBarVariant]: ReturnType<typeof css>;
} = {
  'full-size': css`
    flex: 1;
    align-self: stretch;
    padding: ${({ isPadding }: WrapperProps) => (isPadding ? css`20px 60px 30px 50px` : '0')};
    width: calc(100% - ${PROJECT_PAGE_NAVBAR_WIDTH});
    max-width: calc(100% - ${PROJECT_PAGE_NAVBAR_WIDTH});
  `,
  split: css`
    width: ${DASHBOARD_CONTENT_WRAPPER_WIDTH}px;
    border-right: 1px solid #ccdde6;
    flex-shrink: 0;
    padding: 0 24px;
  `,
  'no-content': css`
    display: none;
    & + ${RightPanelWrapper} {
      max-width: calc(100% - ${PROJECT_PAGE_NAVBAR_WIDTH}) !important;
      border: 0 none !important;
      ${rightPanelWrapperOnTablet};
    }
  `,
  default: css`
    width: 100%;
    padding: 0;
  `
};

export const SelectContainer = styled.div<{ isUserFree: boolean }>`
  width: 100%;
  ${SelectWrapper} {
    width: inherit;
  }
  ${SelectOptionsContainer} {
    top: 60px;
  }
  ${OptionContainer} {
    padding: 0 14px;
    + div {
      width: 100%;
    }
    ${TooltipContent} {
      left: 0;
      min-width: 216px;
      line-height: 20px;
      padding: 15px 20px;
      background-color: #fff;
      transform: translate(20px, calc(-100% - 10px));
      span,
      a {
        margin: 0;
        padding: 0;
        color: #292a2b;
        display: inline;
        min-height: unset;
        line-height: 20px;
      }
      a {
        color: var(--blue_100);
        margin-left: ${({ isUserFree }) => (isUserFree ? 6 : 0)}px;
      }
    }
  }
  ${SelectValueContainer} {
    height: 50px;
    color: #292a2b;
    font-size: 14px;
    line-height: 17px;
    padding-left: 14px;
    padding-right: 12px;
    font-weight: bold;
    &:after {
      margin: 0;
    }

    &:before {
      filter: ${blackFilter};
    }
  }
`;

export const LayoutButtonsWrapper = styled.div<{ hasRightContent?: boolean }>`
  ${({ hasRightContent }) =>
    hasRightContent &&
    css`
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      & > div {
        flex-grow: 1;
        min-width: 198px;
      }
    `}
`;

export const StickyWrapper = styled.div`
  ${!isMobileOrIPad() &&
    css`
      position: sticky;
      top: 140px;
      height: 100%;
    `}
`;
